(() => {
  const namespace = 'ArmourReviewUI';

  const normalize = value => String(value || '')
    .toLocaleLowerCase('en-IN')
    .replace(/[\u2012-\u2015]/g, '-')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const medalLoads = new WeakSet();

  const medalAssetURL = image => {
    if (!image) return null;
    const path = image.dataset.url || '';
    if (!/^[a-z0-9_-]+\/[a-z0-9_.-]+\.svg$/i.test(path)) return null;

    const publicRoot = window.jdgm?.JM_PUBLIC_IMAGE_URL
      || 'https://public-images.judge.me/judgeme/';
    let root;
    try {
      root = new URL(publicRoot);
    } catch {
      return null;
    }
    if (root.protocol !== 'https:' || root.hostname !== 'public-images.judge.me') return null;

    const collection = window.jdgm?.WIDGET_REBRANDING_ENABLED
      ? 'medals-v2-2025-rebranding/'
      : 'medals-v2/';
    const url = new URL(`${collection}${path}`, root);
    url.searchParams.set('auto', 'format');
    return url.href;
  };

  const medalLabel = medal => {
    const title = medal.closest('.jdgm-medal-wrapper')?.getAttribute('title') || '';
    return title.split('.')[0].trim()
      || (medal.getAttribute('aria-label') || '')
        .replace(/^Judge\.me\s+/i, '')
        .replace(/\s+medal$/i, '')
        .trim();
  };

  const loadMedal = medal => new Promise(resolve => {
    const imageHost = medal.querySelector('.jdgm-medal__image[data-url]');
    if (!imageHost) return resolve(false);

    const src = medalAssetURL(imageHost);
    const alt = imageHost.dataset.alt || medal.getAttribute('aria-label') || '';
    if (!src || !alt.startsWith('Judge.me ')) return resolve(false);

    const existing = imageHost.querySelector('img[src]');
    if (existing) {
      try {
        const actual = new URL(existing.currentSrc || existing.src);
        const expected = new URL(src);
        if (
          actual.origin === expected.origin
          && actual.pathname === expected.pathname
          && existing.complete
          && existing.naturalWidth > 0
        ) {
          medal.classList.remove('jdgm--loading');
          return resolve(true);
        }
      } catch {
        /* Replace an invalid or non-URL child with the validated vendor asset. */
      }
    }

    const image = new Image(72, 72);
    let settled = false;
    const finish = success => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      if (success) {
        medal.classList.remove('jdgm--loading');
      } else {
        image.remove();
      }
      resolve(success);
    };
    const timeout = setTimeout(() => finish(false), 6000);

    image.alt = alt;
    image.decoding = 'async';
    image.loading = 'eager';
    image.addEventListener('load', () => finish(image.naturalWidth > 0), { once: true });
    image.addEventListener('error', () => finish(false), { once: true });
    image.src = src;
    imageHost.replaceChildren(image);
    if (image.complete) finish(image.naturalWidth > 0);
  });

  const verifiedIcon = '<svg class="ag-review-verified__icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20.9922 11.1833V8.28029C20.9922 6.64029 20.9922 5.82028 20.5881 5.28529C20.184 4.75029 19.2703 4.49056 17.4429 3.9711C16.1944 3.6162 15.0938 3.18863 14.2145 2.79829C13.0156 2.2661 12.4161 2 11.9922 2C11.5682 2 10.9688 2.2661 9.7699 2.79829C8.89057 3.18863 7.79002 3.61619 6.54152 3.9711C4.71411 4.49056 3.80041 4.75029 3.3963 5.28529C2.99219 5.82028 2.99219 6.64029 2.99219 8.28029V11.1833C2.99219 16.8085 8.05496 20.1835 10.5861 21.5194C11.1932 21.8398 11.4968 22 11.9922 22C12.4876 22 12.7911 21.8398 13.3982 21.5194C15.9294 20.1835 20.9922 16.8085 20.9922 11.1833Z"/><path d="M8.49219 11.8333C8.49219 11.8333 9.36719 11.8333 10.2422 13.5C10.2422 13.5 13.0216 9.33333 15.4922 8.5"/></svg>';

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const formatReviewDate = time => {
    if (!time || time.dataset.agDate === '1') return;
    const raw = time.getAttribute('datetime') || time.getAttribute('data-content') || '';
    const iso = raw.includes('T') ? raw : raw.replace(' ', 'T').replace(' UTC', 'Z');
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return;
    time.textContent = `${date.getUTCDate()} ${months[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
    time.classList.remove('jdgm-spinner');
    time.dataset.agDate = '1';
  };

  const hydrateMedals = root => {
    document.querySelectorAll('.jdgm-medals-wrapper, .ag-review-awards').forEach(el => el.remove());
    return;
    const wrappers = [];
    if (root?.matches?.('.jdgm-medals-wrapper')) wrappers.push(root);
    root?.querySelectorAll?.('.jdgm-medals-wrapper').forEach(wrapper => wrappers.push(wrapper));
    if (!wrappers.length) return;

    const allWrappers = [...document.querySelectorAll('.jdgm-medals-wrapper')];
    const preferred = allWrappers.find(wrapper => wrapper.closest('.ag-review-awards'))
      || allWrappers[0];

    allWrappers.forEach(wrapper => {
      if (wrapper === preferred) return;
      wrapper.classList.remove('ag-review-medals-ready');
      wrapper.classList.add('jdgm-hidden');
      wrapper.setAttribute('aria-hidden', 'true');
    });

    if (!preferred || medalLoads.has(preferred)) return;
    const medals = [...preferred.querySelectorAll('.jdgm-medal')];
    const hasOfficialEntitlements = preferred.dataset.updatedAt
      && medals.length > 0
      && medals.every(medal => (
        medal.dataset.type
        && medal.dataset.tier
        && medal.getAttribute('aria-label')?.startsWith('Judge.me ')
        && medalAssetURL(medal.querySelector('.jdgm-medal__image[data-url]'))
      ));
    if (!hasOfficialEntitlements) return;

    medalLoads.add(preferred);
    medals.forEach(medal => {
      if (medal.querySelector('.ag-review-medal-label')) return;
      const label = medalLabel(medal);
      if (!label) return;
      const text = document.createElement('span');
      text.className = 'ag-review-medal-label';
      text.textContent = label;
      medal.append(text);
    });

    Promise.all(medals.map(loadMedal)).then(results => {
      if (!results.every(Boolean)) return;
      const verified = preferred.querySelector('.jdgm-verified-wrapper');
      if (verified && !verified.textContent.trim()) verified.hidden = true;
      preferred.classList.remove('jdgm-hidden');
      preferred.classList.add('ag-review-medals-ready');
      preferred.removeAttribute('aria-hidden');
      preferred.setAttribute('role', 'region');
      preferred.setAttribute('aria-label', 'Judge.me store awards');
    });
  };

  const addAggregateScore = widget => {
    const summary = widget.querySelector('.jdgm-rev-widg__summary');
    const stars = summary?.querySelector('.jdgm-rev-widg__summary-stars');
    if (!summary || !stars) return;

    const match = (stars.getAttribute('aria-label') || '').match(/([0-5](?:\.\d+)?)/);
    if (!match) return;

    const label = Number(match[1]).toFixed(1);
    let score = summary.querySelector('.ag-review-score');
    if (!score) {
      score = document.createElement('strong');
      score.className = 'ag-review-score';
      stars.insertAdjacentElement('beforebegin', score);
    }
    if (score.textContent !== label) score.textContent = label;
    summary.querySelector('.ag-review-count')?.remove();
  };

  const decorateReview = review => {
    const author = review.querySelector('.jdgm-rev__author');
    const icon = review.querySelector('.jdgm-rev__icon');
    if (icon && author && icon.dataset.agInitial !== '1') {
      const letter = (author.textContent || '').replace(/\s+/g, ' ').trim().charAt(0).toUpperCase();
      if (letter) icon.textContent = letter;
      icon.dataset.agInitial = '1';
    }

    formatReviewDate(review.querySelector('.jdgm-rev__timestamp'));

    /* 2026-10-05 owner: "these images never show up on reviews". Judge.me ships each review photo as
       <img data-src> inside a .jdgm--loading link and relies on its own lazy loader, which never swaps them in
       on this theme (spinner forever). Give every photo a real src with native lazy loading instead. */
    review.querySelectorAll('img.jdgm-rev__pic-img').forEach(img => {
      const link = img.closest('.jdgm--loading');
      const done = () => link?.classList.remove('jdgm--loading');
      if (!img.getAttribute('src') && img.dataset.src) {
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = 160;
        img.height = 160;
        img.addEventListener('load', done, { once: true });
        img.src = img.dataset.src;
      } else if (img.complete && img.naturalWidth) {
        done();
      }
    });

    const badge = review.querySelector('.jdgm-rev__buyer-badge');
    if (review.dataset.verifiedBuyer === 'true' && badge && !badge.querySelector('.ag-review-verified__label')) {
      badge.replaceChildren();
      badge.insertAdjacentHTML('beforeend', `${verifiedIcon}<span class="ag-review-verified__label">Verified buyer</span>`);
    }

    const title = review.querySelector('.jdgm-rev__title');
    const body = review.querySelector('.jdgm-rev__body');
    if (title && body) {
      const titleText = normalize(title.textContent);
      const bodyText = normalize(body.textContent);
      const duplicate = !titleText || (bodyText && (bodyText === titleText || bodyText.startsWith(titleText)));
      title.classList.toggle('ag-review-title-hidden', duplicate);
      if (duplicate) title.setAttribute('hidden', '');
      else title.removeAttribute('hidden');
    }

    if (!body || review.querySelector('.ag-review-more') || body.dataset.agClamp === '1') return;
    body.dataset.agClamp = '1';
    body.classList.remove('ag-review-clamp');
    const fullHeight = body.scrollHeight;
    body.classList.add('ag-review-clamp');
    if (fullHeight <= body.clientHeight + 1) {
      body.classList.remove('ag-review-clamp');
      return;
    }
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'ag-review-more';
    more.textContent = 'Read more';
    more.setAttribute('aria-expanded', 'false');
    body.insertAdjacentElement('afterend', more);
    more.addEventListener('click', () => {
      const open = body.classList.contains('ag-review-clamp');
      body.classList.toggle('ag-review-clamp', !open);
      more.textContent = open ? 'Show less' : 'Read more';
      more.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  };

  const markRatingOnlyReviews = widget => {
    widget.querySelectorAll('.jdgm-rev').forEach(review => {
      const title = normalize(review.querySelector('.jdgm-rev__title')?.textContent);
      const body = review.querySelector('.jdgm-rev__body');
      const bodyText = normalize(body?.textContent);
      const productTitle = normalize(review.dataset.productTitle);
      const isRatingOnly = !title && (!bodyText || bodyText === productTitle);
      const existingNote = review.querySelector('.ag-review-rating-only-note');
      const actions = review.querySelector('.jdgm-rev__actions');

      decorateReview(review);
      review.classList.toggle('ag-review-rating-only', isRatingOnly);
      actions?.classList.toggle(
        'ag-review-actions-empty',
        !actions.querySelector('a, button, [role="button"]')
      );
      if (!isRatingOnly) {
        existingNote?.remove();
        return;
      }
      if (existingNote) return;

      const note = document.createElement('p');
      note.className = 'ag-review-rating-only-note';
      note.textContent = 'Rating only';
      body?.insertAdjacentElement('afterend', note);
    });
  };

  const enhance = widget => {
    addAggregateScore(widget);
    markRatingOnlyReviews(widget);
    widget.classList.add('ag-review-ui-ready');
  };

  const refresh = root => {
    if (root?.matches?.('.jdgm-review-widget')) enhance(root);
    root?.querySelectorAll?.('.jdgm-review-widget').forEach(enhance);
    hydrateMedals(root);
  };

  if (window[namespace]) {
    window[namespace].refresh(document);
    return;
  }

  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      refresh(document);
    });
  });

  window[namespace] = { refresh };
  refresh(document);
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
